// components/BaseRecommendations.tsx
import React, { CSSProperties } from 'react';
import { SeverityLevel } from './utils/asthmaTypes';
import { confirmButton } from '../../components/Buttons';

// === 1. List of screens===
export type BaseRecommendationScenario =
  | 'IMPROVED_FROM_SEVERE'
  | 'MILD_DEFAULT'
  | 'MODERATE_DEFAULT'
  | 'MILD_TO_MODERATE'
  | 'MILD_TO_SEVERE'
  | 'MODERATE_TO_SEVERE'
  | 'SEVERE_AFTER_SECOND_ROUND'
  | 'SEVERE_DEFAULT'
  | 'SEVERE_FINAL'
  | 'MODERATE_TO_MILD';
// === 2. Defining the data structure (configuration) ===
interface ButtonConfig {
  label: string;
  type: 'primary' | 'secondary';
  action: 'next_round' | 'third_round' | 'hospital' | 'release';
}

interface RecommendationConfig {
  title?: string;
  text: string;
  buttons: ButtonConfig[]; // the order of the buttons is the order of the view
}

interface BaseRecommendationsProps {
  scenario: BaseRecommendationScenario;
  activeSeverity: SeverityLevel;
  onStartSecondRound: () => void;
  onStartThirdRound: () => void;
  onExit: () => void;

}

// === 3. The brain: defining the content and buttons for each screen===
const getScenarioConfig = (
  scenario: BaseRecommendationScenario,
  isMild: boolean
): RecommendationConfig => {

  switch (scenario) {

    case 'MODERATE_TO_SEVERE':
      return {
        // Text from image
        text: 'מאחר והמצב הורע ממבינוני לקשה יש לתת טיפול תרופתי נוסף הכולל נוזלים, אדרנלין ועוד.',
        buttons: [
          // Blue primary button: for medication treatment
          { label: 'לטיפול תרופתי', type: 'primary', action: 'next_round' },
          // Secondary buttons: release home, hospitalization
          { label: 'שחרור הביתה', type: 'secondary', action: 'release' },
          { label: 'אשפוז', type: 'secondary', action: 'hospital' },
        ],
      };

    case 'MILD_TO_MODERATE':
      return {
        text: 'מאחר והמצב הורע מרע מקל לבינוני, יש לתת 2 מנות של טיפול תרופתי כולל סטרואידים.', // Update text here as needed
        buttons: [
          // The primary button is now "Continue treatment"
          { label: 'המשך טיפול', type: 'primary', action: 'next_round' },
          { label: 'אשפוז', type: 'secondary', action: 'hospital' },
          { label: 'שחרור הביתה', type: 'secondary', action: 'release' },
        ],
      };
    case 'SEVERE_FINAL':
      return {
        title: 'המלצות להמשך טיפול',
        text: 'במידה ואין שיפור מספק יש לשקול אשפוז בטיפול נמרץ.',
        buttons: [
          { label: 'אשפוז', type: 'primary', action: 'hospital' },
          { label: 'שחרור הביתה', type: 'secondary', action: 'release' },
          { label: 'המשך טיפול', type: 'secondary', action: 'next_round' },
        ],
      };

    case 'MILD_TO_SEVERE':
      return {
        text: 'מאחר והמצב החמיר מקל לקשה יש לתת טיפול תרופתי נוסף כולל סטרואידים ונוזלים ',
        buttons: [
          { label: 'המשך טיפול ', type: 'primary', action: 'next_round' },
          { label: 'אשפוז', type: 'secondary', action: 'hospital' },
          { label: 'שחרור הביתה', type: 'secondary', action: 'release' },

        ],

      };

    case 'IMPROVED_FROM_SEVERE':
      return {
        text: isMild
          ? 'למרות שיש הקלה במצב ההמלצה היא לאשפז'
          : 'למרות שיש הקלה במצב ההמלצה היא לאשפז.',
        buttons: [
          { label: 'אשפוז', type: 'primary', action: 'hospital' },
          { label: 'שחרור הביתה', type: 'secondary', action: 'release' },
          { label: 'המשך טיפול', type: 'secondary', action: 'next_round' },
        ],
      };

    case 'MODERATE_DEFAULT':
      return {
        text: 'מאחר ואיו הטבה במצב בינוני ההמלצה היא לאשפז את החולה.',
        buttons: [
          { label: 'אשפוז', type: 'primary', action: 'hospital' },
          { label: 'שחרור הביתה', type: 'secondary', action: 'release' },
          { label: 'המשך טיפול', type: 'secondary', action: 'next_round' },
        ],
      };

    case 'SEVERE_DEFAULT':
      return {
        title: 'המלצות להמשך טיפול',
        text: 'ההתקף כעת קשה – יש להמשיך טיפול אינטנסיבי ומעקב הדוק.',
        buttons: [
          { label: 'המשך טיפול', type: 'primary', action: 'next_round' },
          { label: 'אשפוז', type: 'secondary', action: 'hospital' },
          { label: 'שחרור הביתה', type: 'secondary', action: 'release' }
        ],
      };

    case 'MODERATE_TO_MILD':
      return {
        text: 'מאחר ומצב החולה הוטב ניתן לשחרר עם המלצות רפואיות',

        buttons: [
          { label: 'שחרור הביתה', type: 'primary', action: 'release' },
          { label: 'המשך טיפול', type: 'secondary', action: 'next_round' },
          { label: 'אשפוז', type: 'secondary', action: 'hospital' },
        ],
      };
    case 'MILD_DEFAULT':
      return {
        text: 'מאחר ומצב החולה נשאר טוב ויציב ניתן לשחרר עם המלצות רפואיות.',
        buttons: [
          { label: 'שחרור הביתה', type: 'primary', action: 'release' },
          { label: 'המשך טיפול', type: 'secondary', action: 'next_round' },
          { label: 'אשפוז', type: 'secondary', action: 'hospital' },
        ],
      };

    case 'SEVERE_AFTER_SECOND_ROUND':
      return {
        text: 'מאחר והמצב הורע ומוגדר כעת כהתקף קשה, יש לתת טיפול תרופתי נוסף הכולל מגנזיום, אדרנלין ועוד.',
        buttons: [
          { label: 'אשפוז', type: 'primary', action: 'hospital' },
          { label: 'שחרור הביתה', type: 'secondary', action: 'release' },
          { label: 'המשך טיפול', type: 'secondary', action: 'next_round' },
        ],
      };

    default:
      return { text: '', buttons: [] };
  }
};

// === 4. generic component===
export const BaseRecommendations: React.FC<BaseRecommendationsProps> = ({
  scenario,
  activeSeverity,
  onStartSecondRound,
  onStartThirdRound,
  onExit,

}) => {
  const isMild = activeSeverity === 'MILD';

  // get right configuration
  const config = getScenarioConfig(scenario, isMild);

  // Managing actions
  const handleAction = (action: ButtonConfig['action']) => {
    switch (action) {
      case 'next_round': return onStartSecondRound();
      case 'third_round': return onStartThirdRound();
      case 'hospital': return onExit();
      case 'release': return onExit();
    }
  };

  if (!config.text) return null;

  return (
    <div className="recommendations-card">
      {config.title && <h3 className="recommendations-title">{config.title}</h3>}
      <p className="recommendations-text">{config.text}</p>

      <div className="recommendations-actions">

        {config.buttons
          .filter(b => b.type === 'primary')
          .map((btn, idx) => (
            <button
              key={`p-${idx}`}
              type="button"
              style={confirmButton as CSSProperties}
              onClick={() => handleAction(btn.action)}
            >
              {btn.label}
            </button>
          ))}

        <div className="recommendations-secondary-row">
          {config.buttons
            .filter(b => b.type !== 'primary')
            .map((btn, idx) => (
              <button
                key={`s-${idx}`}
                type="button"
                className="recommendations-secondary-btn"
                onClick={() => handleAction(btn.action)}
              >
                {btn.label}
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};